import React, { useMemo } from 'react';
import { DateRangePicker as MUIDateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { Dayjs } from 'dayjs';
import FormRow from '../FormFields/FormRow/FormRow';
import { Stack } from '@mui/material';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import Event from '@mui/icons-material/Event';

interface Props {
  id: string;
  value: [Dayjs | null, Dayjs | null];
  onChange: (newValue: [Dayjs | null, Dayjs | null]) => void;
  fieldlabel?: string;
  required?: boolean;
  error?: boolean;
  warning?: boolean;
  info?: boolean;
  helperText?: string;
  sx?: any;
  dateFormat?: string;
}

const DateRangePicker = React.memo((props: Props): JSX.Element => {
  // Memoize the computed styles to prevent recreation on every render
  const stackStyles = useMemo(
    () => ({
      width: '300px',
      ...props?.sx,
    }),
    [props?.sx],
  );

  const datePickerStyles = useMemo(
    () => ({
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: props.error ? 'error.main' : 'primary.main',
        },
        '&:hover fieldset': {
          borderColor: props.error ? 'error.main' : 'primary.main',
        },
      },
    }),
    [props.error],
  );

  // Memoize the slot props to prevent object recreation
  const slotProps = useMemo(
    () => ({
      openPickerIcon: {
        sx: {
          color: 'action.active',
        },
      },
      textField: {
        inputProps: {
          placeholder: props.dateFormat || 'DD/MM/YYYY',
        },
      },
    }),
    [props.dateFormat],
  );

  // Memoize the slots object
  const slots = useMemo(
    () => ({
      openPickerIcon: Event,
    }),
    [],
  );

  // Memoize the locale text
  const localeText = useMemo(
    () => ({
      start: 'Start date',
      end: 'End date',
    }),
    [],
  );

  const dateRangePicker = useMemo(
    () => (
      <Stack sx={stackStyles}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={CurrentAppConfig.DefaultLocale}>
          <MUIDateRangePicker
            value={props.value}
            onChange={props.onChange}
            format={props.dateFormat || 'DD/MM/YYYY'}
            slots={slots}
            slotProps={slotProps}
            localeText={localeText}
            sx={datePickerStyles}
          />
        </LocalizationProvider>
      </Stack>
    ),
    [props.value, props.onChange, props.dateFormat, stackStyles, slotProps, slots, localeText, datePickerStyles],
  );

  if (props.fieldlabel) {
    return (
      <FormRow id={props.id} required={props.required} fieldlabel={props.fieldlabel}>
        {dateRangePicker}
      </FormRow>
    );
  }

  return dateRangePicker;
});

DateRangePicker.displayName = 'DateRangePicker';

export default DateRangePicker;
